import { calcularFactura } from "@/lib/billing/factura-de-modulos"
import { PRECIOS_POR_MODULO, type ModuloVendible } from "@/lib/billing/precios"
import { CUANTOS_POR_AREA, porcentajeDelPack } from "@/lib/billing/packs-de-area"
import type { AreaDeModulo } from "@/lib/modules/areas"
import { AREAS_EN_ORDEN } from "./catalogo"

/**
 * El precio que enseña la web, con la misma cuenta que se cobra.
 *
 * No hay otra aritmética: `calcularFactura` es la que usa la suscripción y la
 * que se le manda a Stripe. Aquí solo se agrupa por área para pintarlo.
 */

export interface LineaDeArea {
  area: AreaDeModulo
  modulos: number
  /** Lo que paga el área, con el pack ya descontado, en céntimos. */
  centimos: number
  pack: number
}

export interface PrecioDeLanding {
  totalCentimos: number
  /** Lo que costaría sin packs: para enseñar el ahorro. */
  brutoCentimos: number
  lineas: LineaDeArea[]
}

export function precioMensual(modulos: readonly ModuloVendible[], empleados: number): PrecioDeLanding {
  const factura = calcularFactura({ modulos, empleados, intervalo: "mensual" })
  const lineas: LineaDeArea[] = []
  for (const area of AREAS_EN_ORDEN) {
    const deArea = factura.lineas.filter((l) => l.area === area)
    if (deArea.length === 0) continue
    const base = deArea.reduce((s, l) => s + l.centimosLinea, 0)
    const descuento = factura.descuentos.find((d) => d.area === area)
    lineas.push({ area, modulos: deArea.length, centimos: base - (descuento?.centimos ?? 0), pack: descuento?.porcentaje ?? 0 })
  }
  const brutoCentimos = factura.lineas.reduce((s, l) => s + l.centimosLinea, 0)
  return { totalCentimos: factura.totalCentimos, brutoCentimos, lineas }
}

/** Cuántos le faltan a un área para el siguiente pack, o null si ya tiene el máximo. */
export function siguientePack(area: AreaDeModulo, encendidos: number): { faltan: number; porcentaje: number } | null {
  const total = CUANTOS_POR_AREA[area]
  if (porcentajeDelPack(encendidos, total) === 30) return null
  if (encendidos >= 3) return { faltan: total - encendidos, porcentaje: 30 }
  return { faltan: 3 - encendidos, porcentaje: 20 }
}

export function areaDe(modulo: ModuloVendible): AreaDeModulo {
  return PRECIOS_POR_MODULO[modulo].area
}

/** 6050 → «60,50 €». */
export function euros(centimos: number): string {
  return `${(centimos / 100).toFixed(2).replace(".", ",")} €`
}

/** El precio unitario de un módulo, como se lee en la web. */
export function precioUnitario(modulo: ModuloVendible): string {
  const p = PRECIOS_POR_MODULO[modulo]
  return p.tipo === "por_empleado" ? `${euros(p.centimosMensuales)} / persona / mes` : `${euros(p.centimosMensuales)} / mes`
}
